import { api } from "../api";
import { WORKOUT_TYPES } from "./types";
import {
  AllWorkoutResponse,
  GenericWorkout,
  Workout,
  WorkoutContainer,
  WorkoutContainerFromApi,
  WorkoutPreview,
  WorkoutTemplatePreview,
  WorkoutsResponse,
  WorkoutsWeek,
} from "./types";

// Entry restituita da GET /workout/history/{userId}: include sia schede attive
// sia soft-deleted (con deletedAt valorizzato).
export type WorkoutHistoryEntry = {
  id: string;
  name?: string;
  workoutType?: keyof typeof WORKOUT_TYPES;
  createdAt?: number | null;
  deletedAt?: number | null;
};

const extendedApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getWorkout: builder.query<Workout, string>({
      query: (workoutId) => ({
        url: "/workout/" + workoutId,
        method: "GET",
      }),
      transformResponse: transformWorkoutResponse,
      providesTags: ["Workout"],
    }),
    updateWorkout: builder.mutation<WorkoutsResponse, Workout>({
      query: (workout) => ({
        url: "/workout/",
        method: "PUT",
        body: transformWorkoutRequest(workout),
      }),
      invalidatesTags: ["Workout"],
    }),
    deleteWorkout: builder.mutation<WorkoutsResponse, Workout>({
      query: (workout) => ({
        url: "/workout/" + workout.id,
        method: "DELETE",
      }),
      // Soft delete lato backend: la scheda sparisce dal tab Workout ma resta
      // nello Storico. Invalido anche "Workout" (cache della scheda singola)
      // altrimenti riaprendola si vede ancora la versione con deletedAt=null.
      invalidatesTags: ["Workout", "Workouts", "User", "WorkoutHistory"],
    }),
    // Hard delete definitivo: usato dallo Storico per cancellare per davvero
    // una scheda. Endpoint dedicato lato BE: DELETE /workout/{id}/permanent.
    hardDeleteWorkout: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `/workout/${id}/permanent`,
        method: "DELETE",
      }),
      invalidatesTags: ["Workout", "Workouts", "User", "WorkoutHistory"],
    }),
    // Storico completo di un utente: include schede soft-deleted (con
    // deletedAt). Endpoint dedicato, lo storico non si appoggia piu' a
    // user.workouts (che ora esclude le cancellate).
    getUserWorkoutHistory: builder.query<WorkoutHistoryEntry[], string>({
      query: (userId) => ({
        url: `/workout/history/${userId}`,
        method: "GET",
      }),
      transformResponse: (response: WorkoutHistoryEntry[]) =>
        response.map((entry) => ({
          ...entry,
          id: String(entry.id),
        })),
      providesTags: ["WorkoutHistory"],
    }),
    createWorkout: builder.mutation<WorkoutsResponse, Partial<Workout>>({
      query: (workout) => ({
        url: "/workout/",
        method: "POST",
        body: transformWorkoutRequest(workout),
      }),
      invalidatesTags: ["Workouts", "Users"],
    }),
    getAllWorkout: builder.query<WorkoutPreviewResponse, void>({
      query: () => ({
        url: "/workout",
        method: "GET",
      }),
      transformResponse: (response: AllWorkoutResponse) =>
        transformAllWorkoutResponse(response),
      providesTags: ["Workouts"],
    }),
    // Sposta una scheda dentro/fuori da una cartella senza toccare il suo
    // contenuto (data): endpoint dedicato, NON updateWorkout (le preview non
    // hanno il campo data).
    moveWorkoutToContainer: builder.mutation<
      void,
      { id: string | number; idContainer: number | null }
    >({
      query: ({ id, idContainer }) => ({
        url:
          `/workout/${id}/container` +
          (idContainer != null ? `?idContainer=${idContainer}` : ""),
        method: "PUT",
      }),
      invalidatesTags: ["Workouts"],
    }),
    // --- Cartelle workout (con sotto-cartelle) ---
    getAllWorkoutContainers: builder.query<WorkoutContainer[], void>({
      query: () => ({ url: "/workout-container", method: "GET" }),
      transformResponse: (response: WorkoutContainerFromApi[]) =>
        response.map((c) => ({
          id: c.id,
          name: c.name,
          color: c.color,
          parentId: c.parentId ?? null,
        })),
      providesTags: ["WorkoutContainers"],
    }),
    createWorkoutContainer: builder.mutation<
      WorkoutContainer,
      { name: string; color?: string; parentId?: number | null }
    >({
      query: (container) => ({
        url: "/workout-container",
        method: "POST",
        body: container,
      }),
      transformResponse: (response: WorkoutContainerFromApi) => ({
        id: response.id,
        name: response.name,
        color: response.color,
        parentId: response.parentId ?? null,
      }),
      invalidatesTags: ["WorkoutContainers"],
    }),
    updateWorkoutContainer: builder.mutation<WorkoutContainer, WorkoutContainer>({
      query: (container) => ({
        url: "/workout-container",
        method: "PUT",
        body: container,
      }),
      transformResponse: (response: WorkoutContainerFromApi) => ({
        id: response.id,
        name: response.name,
        color: response.color,
        parentId: response.parentId ?? null,
      }),
      invalidatesTags: ["WorkoutContainers"],
    }),
    deleteWorkoutContainer: builder.mutation<
      void,
      { id: number; deleteWorkouts: boolean }
    >({
      query: ({ id, deleteWorkouts }) => ({
        url: `/workout-container/${id}?deleteWorkouts=${deleteWorkouts}`,
        method: "DELETE",
      }),
      invalidatesTags: ["WorkoutContainers", "Workouts"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetWorkoutQuery,
  useLazyGetWorkoutQuery,
  useGetAllWorkoutQuery,
  useCreateWorkoutMutation,
  useDeleteWorkoutMutation,
  useHardDeleteWorkoutMutation,
  useGetUserWorkoutHistoryQuery,
  useUpdateWorkoutMutation,
  useMoveWorkoutToContainerMutation,
  useGetAllWorkoutContainersQuery,
  useCreateWorkoutContainerMutation,
  useUpdateWorkoutContainerMutation,
  useDeleteWorkoutContainerMutation,
} = extendedApi;

function transformAllWorkoutResponse(response: AllWorkoutResponse) {
  return {
    workouts: response.filter((workout) => workout.workoutType === "DEFAULT"),
    templates: response.filter((workout) => workout.workoutType === "TEMPLATE"),
  } as WorkoutPreviewResponse;
}

type WorkoutPreviewResponse = {
  workouts: WorkoutPreview[];
  templates: WorkoutTemplatePreview[];
};

function transformWorkoutResponse(workout: WorkoutsResponse): Workout {
  let data = [] as WorkoutsWeek[];
  try {
    data = JSON.parse(workout.data) as WorkoutsWeek[];
    //if data is not an array, force it to be an array
    if (!Array.isArray(data)) {
      data = [];
    }
  } catch (error) {
    console.error(error);
  }
  return {
    ...workout,
    data: data || [],
  };
}

function transformWorkoutRequest(workout: Workout | Partial<Workout>) {
  let data = "";
  try {
    data = JSON.stringify(workout.data);
  } catch (error) {
    console.error(error);
  }
  return {
    ...workout,
    data: data,
    id: workout.id || null,
    // Mantieni esplicitamente deletedAt: il BE ha preserveDeletedAt come
    // safety net, ma esplicito lato FE evita "resurrezione" silenziosa nel
    // caso in cui lo slice perdesse il campo (defense-in-depth).
    deletedAt: workout.deletedAt ?? null,
    weeks: [],
    // weeks:
    //   workout.weeks?.map((week) => ({
    //     ...week,
    //     id: week.id || null,
    //     days:
    //       week.days?.map((day) => ({
    //         ...day,
    //         exercises:
    //           day.exercises?.map((exercise) => ({
    //             ...exercise,
    //             id: exercise.id || null,
    //           })) || [],
    //         id: day.id || null,
    //       })) || [],
    //   })) || [],
  };
}
